import { StackGrid } from "@data-display/layout/StackGrid";
import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import { TextFieldForm } from "@inputs/hook-form";
import { PhoneInputV2 } from "@inputs/text/PhoneInputV2";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContactsIcon from "@mui/icons-material/ContactsOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Controller, type ControllerRenderProps, type FieldErrors, useForm } from "react-hook-form";
import { ContactFormActionButtons } from "./actionButton";
import { type TModalFormContactResolver, formContactResolver } from "./resolver";
import { ModalContactPaperCollapse, classes } from "./styles";
import { ContactFormTitle } from "./title";

export const ModalFormFieldContact = ({
  errors,
  value,
  onChange,
  disabled,
}: {
  errors: FieldErrors<{ contact: TModalFormContactResolver }>;
  value: TModalFormContactResolver | null;
  onChange: ControllerRenderProps<TModalFormContactResolver>["onChange"];
  disabled?: boolean;
}) => {
  const {
    control: contactControl,
    reset,
    handleSubmit,
    formState: { errors: contactErrors },
  } = useForm<TModalFormContactResolver>({
    resolver: formContactResolver,
  });
  const [open, setOpen] = useState(false);

  return (
    <ModalContactPaperCollapse
      className={classes.root}
      open={Boolean(errors.contact ?? contactErrors.phone ?? contactErrors.email) || open}
      onOpen={setOpen}
      category={{
        icon: <ContactsIcon htmlColor="white" />,
        iconBackgroundColor: (theme) => theme.palette.success.main,
      }}
      {...(value
        ? {
            title: <ContactFormTitle value={value} />,
            actionButtons: <ContactFormActionButtons setOpen={setOpen} open={open} onChange={onChange} reset={reset} />,
            disabled: true,
            fullWidth: true,
          }
        : {
            title: <Typography variant="subtitle2SemiBold">Contact</Typography>,
            openIcon: {
              open: <AddIcon />,
              close: <RemoveIcon />,
            },
            disabled,
          })}
    >
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <StackGrid columnGap={2} rowGap={2} perLine={2}>
          <TextFieldForm
            control={contactControl}
            name="firstName"
            defaultValue={value?.firstName ?? ""}
            label="Prénom"
            size="small"
          />
          <TextFieldForm
            control={contactControl}
            name="lastName"
            defaultValue={value?.lastName ?? ""}
            label="Nom"
            size="small"
          />
          <TextFieldForm
            control={contactControl}
            name="job"
            defaultValue={value?.job ?? ""}
            label="Poste"
            size="small"
          />
          <TextFieldForm
            control={contactControl}
            name="email"
            defaultValue={value?.email ?? ""}
            error={Boolean(errors?.contact?.email)}
            hideHelperText
            label="Email"
            size="small"
          />
        </StackGrid>
        <Controller
          name="phone"
          control={contactControl}
          defaultValue={value?.phone ?? ""}
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <PhoneInputV2
              {...props}
              inputRef={ref}
              label="Téléphone"
              size="small"
              error={errors?.contact?.phone ? true : Boolean(error)}
              helperText={error && error.type !== "required" ? error.message : undefined}
              fullWidth
            />
          )}
        />
        <Typography variant="caption">Pour un contact, il faut a minima un email ou un téléphone.</Typography>
        <Stack
          direction="row"
          sx={{
            justifyContent: "end",
            gap: 1,
          }}
        >
          <IconButtonVariant
            variant="elevated"
            onClick={() => {
              reset(value ?? undefined);
              setOpen(false);
            }}
          >
            <CloseIcon color="primary" />
          </IconButtonVariant>
          <IconButtonVariant
            variant="elevated"
            onClick={handleSubmit((data) => {
              onChange(data);
              setOpen(false);
            })}
          >
            <CheckIcon color="success" />
          </IconButtonVariant>
        </Stack>
      </Stack>
    </ModalContactPaperCollapse>
  );
};
